/* eslint-disable react/prop-types, jsx-a11y/anchor-has-content */
import React from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { ABThemeProvider, useErrorNotifications } from '@applyboard/ui-components';
import {
  EventTracker,
  useOnRouteChange,
  sendEventToConsole,
  sendEventToMixpanel,
  sendEventToMetaPixel,
  sendEventToNewrelic,
  sendEventToAirbrake,
  sendDecisionEvent,
  sendUserEventToUiServices,
  useSpeedcurveSnippet,
  getUserId,
  getUserType,
  getTempId,
  getUTCTime,
  getUnixTime,
  FeatureDecisionProvider,
} from '@applyboard/ui-utils';
import { CrystalProvider } from '@applyboard/crystal-ui';
import { RoutesWrapper, DataLayerProvider, ABLink } from '~/ab-common/src';
import { Layout } from '../components/Layout';

import Router from '../router/Router';
import { ROUTES } from '../router/config';
import { localConfig } from '~/config/localFeatureDecision';
import { useStoreLeadSource } from '@applyboard/dsj-ui';
import { LegacyAlertProvider } from '@applyboard/business-ui-legacy';

const CustomLink = React.forwardRef(({ href, ...restProps }, ref) =>
  href[0] === '/' ? (
    <ABLink to={href} ref={ref} {...restProps} />
  ) : (
    <a ref={ref} href={href} {...restProps} />
  ),
);
CustomLink.displayName = 'CustomLink';

const App = () => {
  useStoreLeadSource();

  // determine if current url has fullscreen flag
  const isFullScreen = useRouteMatch({
    path: ROUTES.filter(item => item.fullscreen).map(item => item.path),
  });
  // Dispatches an event each time a page is laoded or route is changed ('pathname', 'search' or 'hash')
  useOnRouteChange();

  useSpeedcurveSnippet(window.SPEEDCURVE_KEY);

  const { errorNotificationQueue, ErrorNotifications } = useErrorNotifications();

  /*
  NOTE: 'query.get('language')' is a workaround to handle legacy pages that use query parameters to specify the language.
  Please do not rely on query parameters to specify the language on new pages. Instead, stucture
  the page to leverage the navigator browser API to respect the users language settings.
  */
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const lang = query.get('language') || navigator.language;

  return (
    <DataLayerProvider errorNotificationQueue={errorNotificationQueue}>
      <RoutesWrapper>
        <FeatureDecisionProvider localConfig={localConfig}>
          <ABThemeProvider>
            <CrystalProvider
              linkAs={CustomLink}
              lang={['fr', 'fr-CA', 'fr-FR'].includes(lang) ? 'fr-CA' : 'en-US'}
            >
              <LegacyAlertProvider>
                <Layout fullscreen={Boolean(isFullScreen)}>
                  <Router routes={ROUTES} />
                </Layout>
              </LegacyAlertProvider>
            </CrystalProvider>
            <ErrorNotifications />
          </ABThemeProvider>
        </FeatureDecisionProvider>
      </RoutesWrapper>
    </DataLayerProvider>
  );
};

// Initilialize UI Event Tracker
const TrackedApp = EventTracker(
  // Define global tracked data that each event will contain
  props => ({
    app: 'PWS SPA',
    page: '',
    user_id: '',
    user_type: '',
    temp_id: getTempId(),
    timestamp: '',
    timestamp_unixtime: '',
    geo_location: props.GeoLocation ? props.GeoLocation : {}, // NOTE: Not used at the moment, geolocation data provided by MixPanel
  }),
  // Dispatch function that defines where the event objects will be sent
  {
    dispatch: event => {
      // Get event timestamp
      const datetime = new Date();
      // Add extra information to the event
      const detailedEvent = {
        ...event,
        page: `${window.location.pathname}`,
        current_url: {
          origin: `${window.location.origin}`,
          pathname: `${window.location.pathname}`,
          search: `${window.location.search}`,
          hash: `${window.location.hash}`,
        },
        timestamp: getUTCTime(datetime),
        timestamp_unixtime: getUnixTime(datetime),
        user_id: getUserId(),
      };

      const user_type = getUserType();
      if (user_type) {
        detailedEvent.user_type = user_type;
      }

      if (detailedEvent.error) {
        sendEventToNewrelic(detailedEvent);
        sendEventToAirbrake(detailedEvent);
      }

      if (detailedEvent.feature_event_key) {
        sendDecisionEvent(detailedEvent);
      }

      // Multi-channels support (events could be send to multiple destinations)
      sendEventToConsole(detailedEvent); // enabled on localhost only (development environment)
      sendEventToMixpanel(detailedEvent);
      sendEventToMetaPixel(detailedEvent);
      sendUserEventToUiServices(detailedEvent);
    },
  },
)(App);

export default TrackedApp;
